import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { Input } from '@/components/ui/input';
import { useSaveQuestionClass, useSaveQuestionSubject } from '../hooks/useQuestionMutations';
import {
  questionClassFormSchema,
  questionSubjectFormSchema,
  type QuestionClass,
  type QuestionClassFormValues,
  type QuestionSubject,
  type QuestionSubjectFormValues,
} from '../types/question.types';

const CLASS_DEFAULTS: QuestionClassFormValues = {
  name: '',
  code: '',
  nameLocal: '',
  description: '',
  slNum: '',
  isActive: true,
  activeFrom: '',
  activeTo: '',
};

const SUBJECT_DEFAULTS: QuestionSubjectFormValues = {
  name: '',
  code: '',
  nameLocal: '',
  description: '',
  slNum: '',
  isActive: true,
  activeFrom: '',
  activeTo: '',
};

const toDateInput = (value: string | null): string | undefined => {
  if (value === null) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(
        parsed.getDate(),
      ).padStart(2, '0')}`;
};

const numberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

function RequiredMark() {
  return (
    <span aria-hidden className="text-destructive">
      *
    </span>
  );
}

function StatusButtons({ value, onChange }: { value: boolean; onChange: (next: boolean) => void }) {
  return (
    <div className="flex gap-2">
      <Button
        type="button"
        variant={value ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange(true)}
      >
        Active
      </Button>
      <Button
        type="button"
        variant={value ? 'outline' : 'default'}
        size="sm"
        onClick={() => onChange(false)}
      >
        Inactive
      </Button>
    </div>
  );
}

interface ClassModalProps {
  questionClass: QuestionClass | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuestionClassFormModal({ questionClass, open, onOpenChange }: ClassModalProps) {
  const form = useForm<QuestionClassFormValues>({
    resolver: zodResolver(questionClassFormSchema),
    defaultValues: CLASS_DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      questionClass === null
        ? CLASS_DEFAULTS
        : {
            name: questionClass.name,
            code: questionClass.code ?? '',
            nameLocal: questionClass.nameLocal ?? '',
            description: questionClass.description ?? '',
            slNum: questionClass.slNum === null ? '' : String(questionClass.slNum),
            activeFrom: toDateInput(questionClass.activeFrom) ?? '',
            activeTo: toDateInput(questionClass.activeTo) ?? '',
            isActive: questionClass.isActive,
          },
    );
  }, [questionClass, open, form]);

  const save = useSaveQuestionClass(() => onOpenChange(false));

  function submit(values: QuestionClassFormValues) {
    const code = numberOrUndefined(values.code);
    const slNum = numberOrUndefined(values.slNum);

    save.mutate({
      id: questionClass?.id ?? null,
      payload: {
        name: values.name,
        isActive: values.isActive,
        ...(code === undefined ? {} : { code }),
        ...(slNum === undefined ? {} : { slNum }),
        ...(values.nameLocal.trim() === '' ? {} : { nameLocal: values.nameLocal.trim() }),
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
        ...(values.activeFrom === '' ? {} : { activeFrom: values.activeFrom }),
        ...(values.activeTo === '' ? {} : { activeTo: values.activeTo }),
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{questionClass === null ? 'Add Class' : 'Edit Class'}</DialogTitle>
          <DialogDescription>The classes question projects are declared against.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <RequiredMark />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. Ten" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. 10"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nameLocal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Local Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="activeFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Active From</FormLabel>
                    <FormControl>
                      <DatePickerInput
                        id="qcf-active-from"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="activeTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Active To</FormLabel>
                    <FormControl>
                      <DatePickerInput
                        id="qcf-active-to"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="slNum"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sl Num</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Optional"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <StatusButtons value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                {questionClass === null ? 'Create' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

interface SubjectModalProps {
  subject: QuestionSubject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuestionSubjectFormModal({ subject, open, onOpenChange }: SubjectModalProps) {
  const form = useForm<QuestionSubjectFormValues>({
    resolver: zodResolver(questionSubjectFormSchema),
    defaultValues: SUBJECT_DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      subject === null
        ? SUBJECT_DEFAULTS
        : {
            name: subject.name,
            code: subject.code ?? '',
            nameLocal: subject.nameLocal ?? '',
            description: subject.description ?? '',
            slNum: subject.slNum === null ? '' : String(subject.slNum),
            isActive: subject.isActive,
            activeFrom: toDateInput(subject.activeFrom) ?? '',
            activeTo: toDateInput(subject.activeTo) ?? '',
          },
    );
  }, [subject, open, form]);

  const save = useSaveQuestionSubject(() => onOpenChange(false));

  function submit(values: QuestionSubjectFormValues) {
    const slNum = numberOrUndefined(values.slNum);

    save.mutate({
      id: subject?.id ?? null,
      payload: {
        name: values.name,
        isActive: values.isActive,
        ...(values.code.trim() === '' ? {} : { code: values.code.trim() }),
        ...(slNum === undefined ? {} : { slNum }),
        ...(values.nameLocal.trim() === '' ? {} : { nameLocal: values.nameLocal.trim() }),
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
        ...(values.activeFrom === '' ? {} : { activeFrom: values.activeFrom }),
        ...(values.activeTo === '' ? {} : { activeTo: values.activeTo }),
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{subject === null ? 'Add Subject' : 'Edit Subject'}</DialogTitle>
          <DialogDescription>
            The subjects a class carries, and the papers come in against.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <RequiredMark />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. Physics 1st Paper" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. PHY1"
                      autoComplete="off"
                      className="tabular-nums"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nameLocal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Local Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="activeFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Active From</FormLabel>
                    <FormControl>
                      <DatePickerInput
                        id="qsf-active-from"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="activeTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Active To</FormLabel>
                    <FormControl>
                      <DatePickerInput
                        id="qsf-active-to"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="slNum"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sl Num</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Optional"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <StatusButtons value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                {subject === null ? 'Create' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
